import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Plus, Heart } from "lucide-react";
import CommentThread from "../components/CommentThread";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORK_BG = {
  backgroundColor: "#c39a6b",
  backgroundImage:
    "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.06) 0 2px, transparent 3px), radial-gradient(circle at 70% 60%, rgba(0,0,0,0.05) 0 2px, transparent 3px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.05) 0 2px, transparent 3px), radial-gradient(circle at 85% 20%, rgba(0,0,0,0.05) 0 2px, transparent 3px)",
  backgroundSize: "26px 26px, 32px 32px, 28px 28px, 22px 22px",
};

const Pushpin = () => (
  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
    <span className="block h-4 w-4 rounded-full bg-red-600 shadow-[0_2px_4px_rgba(0,0,0,0.4)] ring-2 ring-red-800/40" />
  </span>
);

const StickyNote = ({ entry, onClick }) => {
  const [reacted, setReacted] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(`bulletin_react_${entry.id}`) === "1"
  );
  const [count, setCount] = useState(entry.reactions || 0);

  const react = async (e) => {
    e.stopPropagation();
    const delta = reacted ? -1 : 1;
    const next = !reacted;
    setReacted(next);
    setCount((c) => Math.max(0, c + delta));
    localStorage.setItem(`bulletin_react_${entry.id}`, next ? "1" : "0");
    try {
      await axios.post(`${API}/community/entries/${entry.id}/react`, { delta });
    } catch (err) {
      // revert on failure
      setReacted(!next);
      setCount((c) => Math.max(0, c - delta));
    }
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      data-testid={`bulletin-note-${entry.id}`}
      className="relative block w-full text-left p-6 pt-7 rounded-sm shadow-[3px_5px_14px_rgba(0,0,0,0.22)] cursor-pointer transition-transform duration-300 hover:-translate-y-1.5 hover:rotate-0 hover:shadow-[5px_9px_22px_rgba(0,0,0,0.3)]"
      style={{ backgroundColor: entry.color, transform: `rotate(${entry.rotation || 0}deg)` }}
    >
      <Pushpin />
      <p className="font-display text-neutral-800 text-lg leading-snug whitespace-pre-line line-clamp-6">
        {entry.story}
      </p>
      <div className="mt-5 pt-3 border-t border-black/10">
        <span className="font-sans-ui text-xs uppercase tracking-widest text-neutral-700 block">
          {entry.initials} &middot; {entry.age} &middot; {entry.location}
        </span>
        <div className="flex items-center gap-4 mt-2.5">
          <button
            onClick={react}
            data-testid={`bulletin-react-${entry.id}`}
            aria-label="React with a heart"
            title="Send some love"
            className={`inline-flex items-center gap-1.5 text-xs font-sans-ui rounded-full px-2.5 py-1 transition-all duration-200 hover:scale-105 ${
              reacted ? "bg-rose-100 text-rose-600" : "text-neutral-600 hover:bg-black/5"
            }`}
          >
            <Heart size={14} fill={reacted ? "currentColor" : "none"} />
            {count > 0 && <span>{count}</span>}
          </button>
          <span className="inline-flex items-center gap-1 text-neutral-500 text-xs">
            <MessageCircle size={13} />
            {entry.comments ? entry.comments.length : 0}
          </span>
        </div>
      </div>
    </div>
  );
};

const SubmitForm = ({ onDone }) => {
  const [form, setForm] = useState({ initials: "", age: "", location: "", story: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.initials.trim() || !form.age || !form.location.trim() || !form.story.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/community/submit`, {
        initials: form.initials.trim(),
        age: parseInt(form.age, 10),
        location: form.location.trim(),
        story: form.story.trim(),
      });
      toast.success(res.data.message || "Thanks! Your story is pending approval.");
      setForm({ initials: "", age: "", location: "", story: "" });
      onDone();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <Input
          value={form.initials}
          onChange={set("initials")}
          maxLength={10}
          placeholder="Initials"
          data-testid="submit-initials"
          className="h-12 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
        />
        <Input
          type="number"
          min="1"
          max="120"
          value={form.age}
          onChange={set("age")}
          placeholder="Age"
          data-testid="submit-age"
          className="h-12 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
        />
        <Input
          value={form.location}
          onChange={set("location")}
          maxLength={100}
          placeholder="City / Country"
          data-testid="submit-location"
          className="h-12 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
        />
      </div>
      <div>
        <Textarea
          value={form.story}
          onChange={set("story")}
          maxLength={1000}
          rows={6}
          placeholder="Share what you're going through, a lesson, a win, or a struggle in your 20s..."
          data-testid="submit-story"
          className="bg-white/70 border-neutral-300 font-display text-base resize-none focus-visible:ring-neutral-900"
        />
        <p className="text-right font-sans-ui text-xs text-neutral-500 mt-1">
          {form.story.length}/1000
        </p>
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="submit-story-button"
        className="bg-neutral-900 hover:bg-neutral-700 disabled:opacity-60 text-white font-sans-ui py-4 rounded-full transition-colors"
      >
        {loading ? "Sending\u2026" : "Submit for review"}
      </button>
      <p className="font-sans-ui text-xs text-neutral-500 text-center">
        Every story is reviewed before it appears on the board.
      </p>
    </form>
  );
};

const StoryDetail = ({ entry, onCommentAdded }) => {
  const [comments, setComments] = useState(entry.comments || []);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email to comment.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/community/entries/${entry.id}/comment`, {
        name: name.trim(),
        email: email.trim(),
        text: text.trim(),
      });
      const updated = [...comments, res.data];
      setComments(updated);
      setText("");
      setName("");
      setEmail("");
      onCommentAdded(entry.id, updated);
    } catch (err) {
      toast.error("Couldn't post your comment. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div
        className="rounded-sm p-6 mb-6 shadow-inner"
        style={{ backgroundColor: entry.color }}
      >
        <p className="font-display text-neutral-800 text-xl leading-relaxed whitespace-pre-line">
          {entry.story}
        </p>
        <p className="mt-5 font-sans-ui text-xs uppercase tracking-widest text-neutral-700">
          {entry.initials} &middot; {entry.age} &middot; {entry.location}
        </p>
      </div>

      <p className="font-sans-ui text-sm uppercase tracking-widest text-neutral-500 mb-4">
        {comments.length} {comments.length === 1 ? "comment" : "comments"}
      </p>

      <div className="max-h-72 overflow-y-auto pr-1">
        <CommentThread
          comments={comments}
          setComments={setComments}
          apiBase={`${API}/community/entries/${entry.id}`}
        />
      </div>

      <form onSubmit={addComment} className="mt-6 flex flex-col gap-3 border-t border-neutral-300 pt-5">
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Name (optional)"
            data-testid="comment-name"
            className="h-11 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
          />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (required)"
            required
            data-testid="comment-email"
            className="h-11 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
          />
        </div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="Leave a supportive comment..."
          data-testid="comment-text"
          className="bg-white/70 border-neutral-300 font-display resize-none focus-visible:ring-neutral-900"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="comment-submit"
          className="self-end bg-neutral-900 hover:bg-neutral-700 disabled:opacity-60 text-white font-sans-ui text-sm px-7 py-3 rounded-full transition-colors"
        >
          {loading ? "Posting\u2026" : "Post comment"}
        </button>
      </form>
    </div>
  );
};

const CommunityPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/community/entries`);
      setEntries(res.data);
    } catch (err) {
      console.error("Failed to load community entries", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    load();
  }, [load]);

  const handleCommentAdded = (id, updated) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, comments: updated } : e)));
    setActive((a) => (a && a.id === id ? { ...a, comments: updated } : a));
  };

  return (
    <div className="min-h-screen" data-testid="community-page">
      <SEO title="The Bulletin" description="Real stories from real 20-somethings. Pin yours to the board — and read what others are living through." type="website" />
      <Navbar solid />

      {/* Header */}
      <section className="bg-[#eceae5] pt-32 md:pt-40 pb-10 md:pb-14">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-sans-ui uppercase tracking-[0.25em] text-sm text-neutral-500">
              Community
            </p>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl text-neutral-900 mt-3 leading-[1.05]"
              style={{ fontWeight: 700 }}
            >
              Group Chat
            </h1>
            <p className="font-display italic text-neutral-600 text-lg md:text-xl mt-4 max-w-xl">
              Real stories from real 20-somethings. Pin yours to the board &mdash; and read what
              others are living through.
            </p>
          </div>
          <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="open-submit-dialog"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-700 text-white font-sans-ui text-[15px] px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] whitespace-nowrap"
              >
                <Plus size={17} /> Share your story
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#eceae5] max-w-lg border-neutral-300">
              <DialogHeader>
                <DialogTitle className="font-display text-3xl text-neutral-900" style={{ fontWeight: 700 }}>
                  Share your story
                </DialogTitle>
                <DialogDescription className="font-display italic text-neutral-600 text-base">
                  Initials, age, and where you're from &mdash; then whatever's on your heart.
                </DialogDescription>
              </DialogHeader>
              <SubmitForm onDone={() => setSubmitOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Cork board */}
      <section style={CORK_BG} className="border-y-[10px] border-[#7c5a34]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20 min-h-[60vh]">
          {loading ? (
            <p className="font-display text-white/90 text-xl text-center py-20">Loading the board&hellip;</p>
          ) : entries.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-display text-white text-2xl md:text-3xl" style={{ fontWeight: 700 }}>
                The board is empty &mdash; for now.
              </p>
              <p className="font-display italic text-white/85 mt-3">
                Be the first to pin a story. Tap &ldquo;Share your story&rdquo; above.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7 items-start">
              {entries.map((entry) => (
                <StickyNote key={entry.id} entry={entry} onClick={() => setActive(entry)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story detail */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#eceae5] max-w-lg border-neutral-300 max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-neutral-900" style={{ fontWeight: 700 }}>
                  A story from {active.initials}
                </DialogTitle>
                <DialogDescription className="sr-only">Community story and comments</DialogDescription>
              </DialogHeader>
              <StoryDetail entry={active} onCommentAdded={handleCommentAdded} />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CommunityPage;
